import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { IntentLauncher, IntentAction } from '../../components/overview/IntentLauncher';
import { StatCard } from '../../components/overview/StatCard';
import { AttentionStrip, AttentionSignal } from '../../components/overview/AttentionStrip';
import { Reveal } from '../../components/overview/Reveal';
import { IntentImpactSheet } from '../../components/overview/IntentImpactSheet';
import { OnboardingTracker } from '../../components/platform/OnboardingTracker';
import { useParallax } from '../../hooks/useParallax';

interface Overview {
  totals: {
    tenants: number;
    activeTenants: number;
    suspendedTenants: number;
    otherTenants: number;
    events: number;
    staffUsers: number;
    guests: number;
    platformUsers: number;
    venues: number;
    fundraisers: number;
    totalRaised: number;
  };
  moduleAdoption: { module: string; tenants: number }[];
  recentTenants: {
    id: string;
    slug: string;
    name: string;
    status: string;
    createdAt: string;
    hubUrl: string;
    eventCount: number;
    userCount: number;
  }[];
}

interface EventReadiness {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  eventId: string;
  eventName: string;
  status: string;
  percent: number;
  completed: number;
  total: number;
  outstanding: string[];
}

const MODULE_META: Record<string, { icon: string; label: string }> = {
  guests: { icon: '🎟️', label: 'Guest List' },
  ticketing: { icon: '💳', label: 'Ticketing' },
  seating: { icon: '🪑', label: 'Seating' },
  checkin: { icon: '✅', label: 'Check-In' },
  agenda: { icon: '📅', label: 'Agenda' },
  vendors: { icon: '🏪', label: 'Vendors' },
  comms: { icon: '📢', label: 'Comms' },
  website: { icon: '🌐', label: 'Experience' },
  giving: { icon: '💚', label: 'Giving' },
};

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export function PlatformOverviewPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'PlatformAdmin' || user?.role === 'Admin';
  const [data, setData] = useState<Overview | null>(null);
  const [readiness, setReadiness] = useState<EventReadiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { offset, onMouseMove, onMouseLeave } = useParallax(20);
  const [showImpact, setShowImpact] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await apiClient<Overview>('/platform/overview');
        if (!cancelled) setData(d);
        try {
          const r = await apiClient<EventReadiness[]>('/platform/events-onboarding');
          if (!cancelled) setReadiness(r);
        } catch { /* non-critical */ }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load overview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const firstName = (user?.fullName || '').split(' ')[0] || 'there';

  const intents: IntentAction[] = [
    ...(isAdmin ? [{
      key: 'create-org',
      label: 'Create organization',
      description: 'Onboard a new customer workspace.',
      icon: '➕',
      gradient: ['#6366f1', '#8b5cf6'] as [string, string],
      onClick: () => setShowImpact(true),
      featured: true,
    }] : []),
    {
      key: 'manage-orgs',
      label: 'Manage organizations',
      description: 'Browse, drill in, suspend or configure.',
      icon: '🏢',
      gradient: ['#0ea5e9', '#2563eb'],
      onClick: () => navigate('/platform/organizations'),
    },
    {
      key: 'browse-events',
      label: 'Browse events',
      description: 'Every event across all organizations.',
      icon: '📅',
      gradient: ['#10b981', '#059669'],
      onClick: () => navigate('/platform/events'),
    },
    {
      key: 'browse-giving',
      label: 'Browse giving',
      description: 'Every fundraising campaign across all organizations.',
      icon: '💚',
      gradient: ['#16a34a', '#15803d'],
      onClick: () => navigate('/platform/fundraising'),
    },
    {
      key: 'manage-venues',
      label: 'Manage venues',
      description: 'Add, edit, and assign venues.',
      icon: '📍',
      gradient: ['#f59e0b', '#ea580c'],
      onClick: () => navigate('/platform/venues'),
    },
    {
      key: 'manage-system',
      label: isAdmin ? 'System admin' : 'Capabilities',
      description: isAdmin ? 'Health, capabilities, data integrity.' : 'What the platform offers.',
      icon: '🛰️',
      gradient: ['#64748b', '#334155'],
      onClick: () => navigate(isAdmin ? '/platform/admin' : '/platform/capabilities'),
    },
  ];

  return (
    <div>
      <style>{`
        @media (max-width: 820px) {
          .ep-bento { grid-template-columns: 1fr 1fr !important; grid-template-areas: "hero hero" "events guests" "giving venues" !important; }
        }
        @media (max-width: 560px) {
          .ep-bento { grid-template-columns: 1fr !important; grid-template-areas: "hero" "events" "guests" "giving" "venues" !important; }
        }
      `}</style>
      <IntentImpactSheet
        open={showImpact}
        title="Create an organization"
        intro="This usually begins customer onboarding. Here's the path you'll guide them through."
        accent="#6366f1"
        steps={[
          { label: 'Create the organization', hint: 'Name, slug, module ceiling' },
          { label: 'Invite their admin user', hint: 'They manage their own team' },
          { label: 'Create their first event', hint: 'Pick an event-type preset' },
          { label: 'Assign a venue & configure modules', hint: 'Shapes the event setup' },
          { label: 'Add guests & get event-ready', hint: 'Tracked live on the dashboard' },
        ]}
        primaryLabel="Start onboarding →"
        secondaryLabel="Just create the org"
        onPrimary={() => { setShowImpact(false); navigate('/platform/organizations?new=1'); }}
        onSecondary={() => { setShowImpact(false); navigate('/platform/organizations?new=1'); }}
        onClose={() => setShowImpact(false)}
      />

      {/* Hero band */}
      <div style={styles.hero} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
        <div
          style={{ ...styles.heroGlow, transform: `translate(${offset.x}px, ${offset.y}px)` }}
          aria-hidden
        />
        <div style={styles.heroInner}>
          <div style={styles.kicker}>Operator Console</div>
          <h1 style={styles.heroTitle}>
            {greeting()}, {firstName}.
          </h1>
          <p style={styles.heroSub}>
            Here’s your platform at a glance — {data ? data.totals.tenants : '…'} organization
            {data && data.totals.tenants === 1 ? '' : 's'}, {data ? data.totals.events : '…'} event
            {data && data.totals.events === 1 ? '' : 's'} in flight.
          </p>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Intent launcher */}
      <IntentLauncher actions={intents} />

      {loading ? (
        <div style={styles.muted}>Loading metrics…</div>
      ) : data ? (
        <>
          {/* Needs-attention strip — computed signals */}
          <AttentionStrip signals={buildSignals(data, readiness, navigate)} />

          {/* Onboarding tracker — point-in-time journey + event gaps */}
          <OnboardingTracker />

          {/* Bento grid — asymmetric, hero tile dominates */}
          <div style={styles.bento} className="ep-bento">
            {/* Hero tile: Organizations with inline health */}
            <Reveal index={0} style={{ gridArea: 'hero', display: 'flex' }}>
            <button
              type="button"
              style={{ ...styles.bentoTile, ...styles.bentoHero }}
              onClick={() => navigate('/platform/organizations')}
            >
              <span aria-hidden style={styles.bentoHeroGlow} />
              <div style={styles.bentoHeroTop}>
                <span style={styles.bentoHeroChip}>🏢 Organizations</span>
                <span style={styles.bentoHeroCta}>View all →</span>
              </div>
              <div style={styles.bentoHeroValue}>{data.totals.tenants}</div>
              <div style={styles.bentoHeroSub}>
                {data.totals.activeTenants} active · {data.totals.suspendedTenants} suspended
              </div>
              <div style={styles.bentoHeroBar}>
                <div style={{ width: `${data.totals.tenants ? (data.totals.activeTenants / data.totals.tenants) * 100 : 0}%`, background: '#34d399' }} />
                <div style={{ width: `${data.totals.tenants ? (data.totals.suspendedTenants / data.totals.tenants) * 100 : 0}%`, background: '#f87171' }} />
                <div style={{ width: `${data.totals.tenants ? (data.totals.otherTenants / data.totals.tenants) * 100 : 0}%`, background: '#64748b' }} />
              </div>
              <div style={styles.bentoHeroFoot}>{data.totals.platformUsers} platform operator{data.totals.platformUsers === 1 ? '' : 's'}</div>
            </button>
            </Reveal>

            {/* Right column: stacked medium tiles */}
            <Reveal index={1} style={styles.bentoEvents}>
              <StatCard label="Events" value={data.totals.events} icon="📅" accent="#0ea5e9" drillLabel="Browse" onClick={() => navigate('/platform/events')} />
            </Reveal>
            <Reveal index={2} style={styles.bentoGuests}>
              <StatCard label="Guests" value={data.totals.guests} icon="👥" accent="#10b981" hint={`${data.totals.staffUsers} staff users`} drillLabel="View events" onClick={() => navigate('/platform/events')} />
            </Reveal>
            <Reveal index={3} style={styles.bentoGiving}>
              <StatCard label="Giving" value={data.totals.fundraisers} icon="💚" accent="#16a34a" hint={`$${data.totals.totalRaised.toLocaleString('en-US')} raised`} drillLabel="Browse" onClick={() => navigate('/platform/fundraising')} />
            </Reveal>
            <Reveal index={4} style={styles.bentoVenues}>
              <StatCard label="Venues" value={data.totals.venues} icon="📍" accent="#f59e0b" hint="across all orgs" drillLabel="Manage" onClick={() => navigate('/platform/venues')} />
            </Reveal>
          </div>

          <div style={styles.twoCol}>
            {/* Organization health (clickable → orgs) */}
            <section
              style={styles.card}
              role="button"
              tabIndex={0}
              onClick={() => navigate('/platform/organizations')}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate('/platform/organizations'); }}
            >
              <div style={styles.cardHead}>
                <h2 style={styles.h2}>Organization health</h2>
                <span style={styles.cardCta}>Open →</span>
              </div>
              <StatusBar
                active={data.totals.activeTenants}
                suspended={data.totals.suspendedTenants}
                other={data.totals.otherTenants}
                total={data.totals.tenants}
              />
              <div style={styles.statusLegend}>
                <LegendDot color="#10b981" label={`Active ${data.totals.activeTenants}`} />
                <LegendDot color="#ef4444" label={`Suspended ${data.totals.suspendedTenants}`} />
                {data.totals.otherTenants > 0 && <LegendDot color="#94a3b8" label={`Other ${data.totals.otherTenants}`} />}
              </div>
              <div style={styles.platformUsersNote}>
                {data.totals.platformUsers} platform operator{data.totals.platformUsers === 1 ? '' : 's'}
              </div>
            </section>

            {/* Module adoption (clickable → capabilities) */}
            <section
              style={styles.card}
              role="button"
              tabIndex={0}
              onClick={() => navigate('/platform/capabilities')}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate('/platform/capabilities'); }}
            >
              <div style={styles.cardHead}>
                <h2 style={styles.h2}>Module adoption</h2>
                <span style={styles.cardCta}>Capabilities →</span>
              </div>
              {data.moduleAdoption.length === 0 ? (
                <div style={styles.muted}>No modules enabled.</div>
              ) : (
                <div style={styles.adoptionList}>
                  {data.moduleAdoption.slice(0, 6).map((m) => {
                    const meta = MODULE_META[m.module] || { icon: '🧩', label: m.module };
                    const pct = data.totals.tenants ? (m.tenants / data.totals.tenants) * 100 : 0;
                    return (
                      <div key={m.module} style={styles.adoptionRow}>
                        <span style={styles.adoptionLabel}>
                          <span style={{ marginRight: 6 }}>{meta.icon}</span>{meta.label}
                        </span>
                        <div style={styles.adoptionTrack}>
                          <div style={{ ...styles.adoptionFill, width: `${pct}%` }} />
                        </div>
                        <span style={styles.adoptionCount}>{m.tenants}/{data.totals.tenants}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          {/* Recent organizations */}
          <section style={styles.card}>
            <div style={styles.cardHead}>
              <h2 style={styles.h2}>Recent organizations</h2>
              <span style={styles.cardCta} onClick={() => navigate('/platform/organizations')}>View all →</span>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Organization</th>
                  <th style={styles.th}>Address</th>
                  <th style={styles.thNum}>Events</th>
                  <th style={styles.thNum}>Users</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.recentTenants.map((t) => (
                  <tr key={t.id} onClick={() => navigate(`/platform/organizations/${t.slug || t.id}`)} style={styles.rowClickable}>
                    <td style={styles.td}>
                      <div style={styles.tName}>{t.name}</div>
                      <div style={styles.tSlug}>{t.slug}</div>
                    </td>
                    <td style={styles.td}>{t.hubUrl}</td>
                    <td style={styles.tdNum}>{t.eventCount}</td>
                    <td style={styles.tdNum}>{t.userCount}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.statusPill, ...(t.status === 'Active' ? styles.statusActive : styles.statusOther) }}>
                        {t.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Event onboarding readiness */}
          <section style={styles.card}>
            <div style={styles.cardHead}>
              <h2 style={styles.h2}>Event onboarding readiness</h2>
              <span style={styles.cardCta} onClick={() => navigate('/platform/events')}>All events →</span>
            </div>
            {readiness.length === 0 ? (
              <div style={styles.muted}>No events yet.</div>
            ) : (
              <div style={styles.readyList}>
                {readiness.slice(0, 8).map((r) => (
                  <div
                    key={r.eventId}
                    style={styles.readyRow}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/platform/organizations/${r.tenantSlug || r.tenantId}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/platform/organizations/${r.tenantSlug || r.tenantId}`); }}
                  >
                    <div style={styles.readyMeta}>
                      <div style={styles.readyName}>{r.eventName}</div>
                      <div style={styles.readyOrg}>{r.tenantName}</div>
                    </div>
                    <div style={styles.readyBarWrap}>
                      <div style={styles.readyTrack}>
                        <div style={{ ...styles.readyFill, width: `${r.percent}%`, background: r.percent === 100 ? '#10b981' : '#f59e0b' }} />
                      </div>
                      <span style={styles.readyPct}>{r.percent}%</span>
                    </div>
                    <span style={{ ...styles.readyStatus, color: r.outstanding.length ? '#b45309' : '#10b981' }}>
                      {r.outstanding.length ? `${r.outstanding.length} left` : 'Ready ✓'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}

/** Compute "needs attention" signals from the overview + readiness data. */
function buildSignals(
  data: Overview,
  readiness: EventReadiness[],
  navigate: (path: string) => void,
): AttentionSignal[] {
  const signals: AttentionSignal[] = [];

  if (data.totals.suspendedTenants > 0) {
    signals.push({
      id: 'suspended-orgs',
      severity: 'critical',
      icon: '⏸️',
      text: `${data.totals.suspendedTenants} suspended organization${data.totals.suspendedTenants === 1 ? '' : 's'}`,
      onClick: () => navigate('/platform/organizations'),
    });
  }

  const stalled = readiness.filter((r) => r.status !== 'Archived' && r.percent < 50);
  if (stalled.length > 0) {
    signals.push({
      id: 'stalled-events',
      severity: 'warning',
      icon: '🚧',
      text: `${stalled.length} event${stalled.length === 1 ? '' : 's'} under 50% onboarded`,
      onClick: () => navigate(`/platform/organizations/${stalled[0].tenantSlug || stalled[0].tenantId}`),
    });
  }

  const almost = readiness.filter((r) => r.percent >= 50 && r.percent < 100 && r.status !== 'Archived');
  if (almost.length > 0) {
    signals.push({
      id: 'almost-ready',
      severity: 'info',
      icon: '🎯',
      text: `${almost.length} event${almost.length === 1 ? '' : 's'} almost ready`,
      onClick: () => navigate('/platform/events'),
    });
  }

  if (data.totals.events === 0) {
    signals.push({
      id: 'no-events',
      severity: 'info',
      icon: '📅',
      text: 'No events yet — help an organization create one',
      onClick: () => navigate('/platform/events'),
    });
  }

  return signals;
}

function StatusBar({ active, suspended, other, total }: { active: number; suspended: number; other: number; total: number }) {
  const pct = (n: number) => (total ? (n / total) * 100 : 0);
  return (
    <div style={styles.statusTrack} role="img" aria-label={`${active} active, ${suspended} suspended, ${other} other`}>
      <div style={{ width: `${pct(active)}%`, background: '#10b981' }} />
      <div style={{ width: `${pct(suspended)}%`, background: '#ef4444' }} />
      <div style={{ width: `${pct(other)}%`, background: '#94a3b8' }} />
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={styles.legendItem}>
      <span style={{ ...styles.legendDot, background: color }} />
      {label}
    </span>
  );
}

const ACCENT = '#4f46e5';
const BORDER = '#e8eaf0';
const MUTED = '#6b7280';
const INK = '#0b1120';

const styles: Record<string, React.CSSProperties> = {
  hero: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 20,
    padding: '28px 28px',
    marginBottom: 24,
    background: 'linear-gradient(120deg, #0b1120 0%, #1e1b4b 55%, #312e81 100%)',
    color: '#fff',
  },
  heroGlow: {
    position: 'absolute',
    top: -80,
    right: -40,
    width: 300,
    height: 300,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(139,92,246,0.55), transparent 70%)',
    filter: 'blur(10px)',
    pointerEvents: 'none',
    transition: 'transform 0.25s cubic-bezier(.2,.8,.2,1)',
  },
  heroInner: { position: 'relative', zIndex: 1 },
  kicker: { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#a5b4fc' },
  heroTitle: { fontSize: '2rem', fontWeight: 800, margin: '8px 0 6px', letterSpacing: '-0.02em' },
  heroSub: { fontSize: '0.95rem', color: '#c7d2fe', margin: 0, maxWidth: 560, lineHeight: 1.5 },

  bento: {
    display: 'grid',
    gridTemplateColumns: '1.6fr 1fr 1fr',
    gridTemplateRows: 'auto auto',
    gridTemplateAreas: '"hero events guests" "hero giving venues"',
    gap: 16,
    marginBottom: 22,
  },
  bentoTile: { textAlign: 'left', border: 'none', cursor: 'pointer', width: '100%' },
  bentoHero: {
    gridArea: 'hero',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 18,
    padding: '22px 22px 20px',
    background: 'linear-gradient(135deg, #4f46e5 0%, #6d28d9 100%)',
    color: '#fff',
    minHeight: 190,
    boxShadow: '0 10px 30px -14px rgba(79,70,229,0.6)',
  },
  bentoHeroGlow: { position: 'absolute', bottom: -50, right: -30, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.25), transparent 70%)', pointerEvents: 'none' },
  bentoHeroTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 },
  bentoHeroChip: { fontSize: '0.82rem', fontWeight: 700, color: '#e0e7ff' },
  bentoHeroCta: { fontSize: '0.76rem', fontWeight: 700, color: '#c7d2fe' },
  bentoHeroValue: { fontSize: '3.4rem', fontWeight: 800, lineHeight: 1, marginTop: 12, letterSpacing: '-0.03em', position: 'relative', zIndex: 1, fontVariantNumeric: 'tabular-nums' },
  bentoHeroSub: { fontSize: '0.82rem', color: '#ddd6fe', marginTop: 6, position: 'relative', zIndex: 1 },
  bentoHeroBar: { display: 'flex', height: 10, borderRadius: 999, overflow: 'hidden', background: 'rgba(255,255,255,0.2)', marginTop: 14, position: 'relative', zIndex: 1 },
  bentoHeroFoot: { fontSize: '0.76rem', color: '#c7d2fe', marginTop: 12, position: 'relative', zIndex: 1 },
  bentoEvents: { gridArea: 'events', display: 'flex' },
  bentoGuests: { gridArea: 'guests', display: 'flex' },
  bentoGiving: { gridArea: 'giving', display: 'flex' },
  bentoVenues: { gridArea: 'venues', display: 'flex' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20, alignItems: 'start' },
  card: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 16, padding: 18, boxShadow: '0 1px 3px rgba(16,24,40,0.04)', marginBottom: 20 },
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  cardCta: { color: ACCENT, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' },
  h2: { fontSize: '1.05rem', fontWeight: 700, margin: 0, color: INK },

  statusTrack: { display: 'flex', height: 14, borderRadius: 999, overflow: 'hidden', background: '#f1f5f9' },
  statusLegend: { display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#334155', fontWeight: 600 },
  legendDot: { width: 10, height: 10, borderRadius: 3, display: 'inline-block' },
  platformUsersNote: { marginTop: 14, fontSize: '0.8rem', color: MUTED },

  adoptionList: { display: 'flex', flexDirection: 'column', gap: 12 },
  adoptionRow: { display: 'flex', alignItems: 'center', gap: 10 },
  adoptionLabel: { width: 120, fontSize: '0.82rem', fontWeight: 600, color: '#334155' },
  adoptionTrack: { flex: 1, height: 10, background: '#eef2ff', borderRadius: 999, overflow: 'hidden' },
  adoptionFill: { height: '100%', background: `linear-gradient(90deg, ${ACCENT}, #8b5cf6)`, borderRadius: 999 },
  adoptionCount: { width: 46, textAlign: 'right', fontSize: '0.78rem', color: MUTED, fontVariantNumeric: 'tabular-nums' },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: `2px solid ${BORDER}`, color: MUTED, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  thNum: { textAlign: 'right', padding: '8px 10px', borderBottom: `2px solid ${BORDER}`, color: MUTED, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase' },
  td: { padding: '12px 10px', borderBottom: '1px solid #f0f1f5', verticalAlign: 'top' },
  tdNum: { padding: '12px 10px', borderBottom: '1px solid #f0f1f5', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  rowClickable: { cursor: 'pointer' },
  tName: { fontWeight: 700, color: INK },
  tSlug: { color: MUTED, fontSize: '0.8rem', fontFamily: 'monospace' },
  statusPill: { borderRadius: 999, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700 },
  statusActive: { background: '#dcfce7', color: '#166534' },
  statusOther: { background: '#f1f5f9', color: '#475569' },

  readyList: { display: 'flex', flexDirection: 'column', gap: 2 },
  readyRow: { display: 'flex', alignItems: 'center', gap: 14, padding: '10px 8px', borderRadius: 10, cursor: 'pointer' },
  readyMeta: { flex: 1, minWidth: 0 },
  readyName: { fontWeight: 700, color: INK, fontSize: '0.9rem' },
  readyOrg: { color: MUTED, fontSize: '0.78rem' },
  readyBarWrap: { display: 'flex', alignItems: 'center', gap: 8, width: 160 },
  readyTrack: { flex: 1, height: 8, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' },
  readyFill: { height: '100%', borderRadius: 999 },
  readyPct: { fontSize: '0.78rem', color: '#475569', width: 36, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  readyStatus: { fontSize: '0.76rem', fontWeight: 700, width: 64, textAlign: 'right' },

  muted: { color: MUTED, padding: '20px 4px' },
  error: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#7f1d1d', borderRadius: 8, padding: '10px 14px', marginBottom: 16 },
};
